import React, { useState } from 'react';
import { CURRENCY_USD } from 'constants/currency';
import { CoachStatusEnum } from 'types/generated/client';
import { VenueAddress } from 'utils/shared/coachBuilder';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import LessonScheduler from '../LessonScheduler/LessonScheduler';
import PremiumCoachSide from '../PremiumCoachSide';
import PremiumPlayerSide from '../PremiumPlayerSide';
import RemoteLessonScheduler from '../RemoteLessonScheduler/RemoteLessonBooking';
import { Coach, Lessons, Steps } from '../types';

export interface LessonSchedulerProps {
  coachData: Coach;
  lessons: Lessons;
  venueFromMap: VenueAddress | null;
  setVenueFromMap: React.Dispatch<React.SetStateAction<VenueAddress | null>>;
  steps: Steps;
  setSteps: React.Dispatch<React.SetStateAction<Steps>>;
  activeTab: TabsEnum;
  setActiveTab: (activeTab: TabsEnum) => void;
  subscriptionPrice: number;
  playerSubscribedUntil: Date | null;
  onSubscriptionSuccess: () => void;
}

export enum TabsEnum {
  Remote = 'remote',
  Court = 'court',
  Content = 'content',
}

const TabSwitch: React.FC<LessonSchedulerProps> = ({
  coachData,
  lessons,
  venueFromMap,
  setVenueFromMap,
  steps,
  setSteps,
  activeTab,
  setActiveTab,
  subscriptionPrice,
  playerSubscribedUntil,
  onSubscriptionSuccess,
}) => {
  const [showTabSwitch, setShowTabSwitch] = useState<boolean>(true);
  const { user } = useGetCurrentUser();
  const isCoach = user?.coachStatus === CoachStatusEnum.Active;
  const showContentTab = subscriptionPrice > 0;

  return (
    <>
      {showTabSwitch && (
        <div className="flex flex-col px-6 pt-6">
          <div className="relative flex w-full flex-col items-center justify-center whitespace-nowrap rounded-3xl bg-color-bg-lightmode-secondary p-0.5 dark:bg-color-bg-darkmode-secondary xs:w-full">
            <div className="relative flex w-full flex-1 justify-center">
              <div
                className={`absolute bottom-0 left-0 top-0 ${
                  showContentTab ? 'w-1/3' : 'w-1/2'
                } rounded-3xl ${
                  activeTab === TabsEnum.Court
                    ? 'translate-x-0'
                    : activeTab === TabsEnum.Remote
                    ? 'translate-x-[100%]'
                    : 'translate-x-[200%]'
                } bg-color-bg-darkmode-brand `}
              ></div>
              <button
                onClick={() => setActiveTab(TabsEnum.Court)}
                className={`typography-product-caption z-10 flex-1 shrink self-stretch overflow-hidden rounded-3xl px-6 xs:h-8 md:h-8 ${
                  activeTab === TabsEnum.Court
                    ? 'text-color-text-lightmode-invert'
                    : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary'
                }`}
              >
                On-court
              </button>
              <button
                onClick={() => setActiveTab(TabsEnum.Remote)}
                className={`typography-product-caption z-10 flex-1 shrink self-stretch overflow-hidden rounded-3xl px-6 xs:h-8 md:h-8 ${
                  activeTab === TabsEnum.Remote
                    ? 'text-color-text-lightmode-invert'
                    : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary'
                }`}
              >
                Remote
              </button>
              {showContentTab && (
                <button
                  onClick={() => setActiveTab(TabsEnum.Content)}
                  className={`typography-product-caption z-10 flex-1 shrink self-stretch overflow-hidden rounded-3xl px-6 xs:h-8 md:h-8 ${
                    activeTab === TabsEnum.Content
                      ? 'text-color-text-lightmode-invert'
                      : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary'
                  }`}
                >
                  Content
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="">
        {activeTab === TabsEnum.Court && (
          <LessonScheduler
            venueFromMap={venueFromMap}
            coach={coachData}
            lessons={lessons}
            setVenueFromMap={setVenueFromMap}
            showTabSwitch={showTabSwitch}
            setShowTabSwitch={setShowTabSwitch}
          />
        )}
        {activeTab === TabsEnum.Remote && (
          <RemoteLessonScheduler
            coach={coachData}
            showTabSwitch={showTabSwitch}
            setShowTabSwitch={setShowTabSwitch}
            steps={steps}
            setSteps={setSteps}
          />
        )}
        {showContentTab &&
          activeTab === TabsEnum.Content &&
          (isCoach ? (
            <PremiumCoachSide />
          ) : (
            <PremiumPlayerSide
              playerSubscribedUntil={playerSubscribedUntil}
              amount={subscriptionPrice}
              coachId={coachData.id}
              setSteps={setSteps}
              steps={steps}
              toggleTabSwitch={(val) => setShowTabSwitch(val)}
              onSubscriptionSuccess={onSubscriptionSuccess}
              coachName={coachData?.fullName || ''}
              currency={
                coachData?.stripeMerchantCurrency || coachData?.currencyCoachDefault || CURRENCY_USD
              }
            />
          ))}
      </div>
    </>
  );
};

export default TabSwitch;
